import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  Mail, Plus, Copy, Trash2, RefreshCw, LogOut, Settings, 
  Inbox, Clock, User, ChevronRight, Shield, MailOpen, X, Menu
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedAddress) {
      fetchMessages(selectedAddress.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  // Auto-refresh inbox every 10 seconds when an address is selected
  useEffect(() => {
    if (!selectedAddress) return;

    const interval = setInterval(() => {
      fetchMessages(selectedAddress.id);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  const fetchData = async () => {
    try {
      const [addressRes, domainRes] = await Promise.all([
        axios.get(`${API}/emails/my-addresses`, getAuthHeader()),
        axios.get(`${API}/domains`, getAuthHeader())
      ]);
      setAddresses(addressRes.data);
      setDomains(domainRes.data);
      if (addressRes.data.length > 0 && !selectedAddress) {
        setSelectedAddress(addressRes.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (emailId) => {
    try {
      const response = await axios.get(`${API}/emails/${emailId}/messages`, getAuthHeader());
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const createAddress = async () => {
    if (!newUsername || !selectedDomain) {
      toast.error('Please enter a username and select a domain');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/emails/create`, {
        username: newUsername,
        domain_id: selectedDomain
      }, getAuthHeader());
      
      setAddresses([response.data, ...addresses]);
      setSelectedAddress(response.data);
      setNewUsername('');
      setSelectedDomain('');
      setCreateDialogOpen(false);
      toast.success('Email address created!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create address';
      toast.error(message);
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      await axios.delete(`${API}/emails/${addressId}`, getAuthHeader());
      const newAddresses = addresses.filter(a => a.id !== addressId);
      setAddresses(newAddresses);
      if (selectedAddress?.id === addressId) {
        setSelectedAddress(newAddresses[0] || null);
        setMessages([]);
      }
      toast.success('Address deleted');
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/emails/message/${messageId}`, getAuthHeader());
      setMessages(messages.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const viewMessage = async (message) => {
    try {
      const response = await axios.get(`${API}/emails/message/${message.id}`, getAuthHeader());
      setSelectedMessage(response.data);
      // Update local state to mark as read
      setMessages(messages.map(m => m.id === message.id ? { ...m, is_read: true } : m));
    } catch (error) {
      toast.error('Failed to load message');
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Email address copied!');
  };

  const refreshMessages = () => {
    if (selectedAddress) {
      fetchMessages(selectedAddress.id);
      toast.success('Inbox refreshed');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col lg:flex-row overflow-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden p-4 border-b border-border flex items-center justify-between bg-background">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <span className="font-heading font-semibold">Instant-Email</span>
        </div>
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button size="icon" variant="ghost" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 flex flex-col">
            <SheetHeader className="p-4 border-b border-border flex-shrink-0">
              <SheetTitle className="font-heading text-left">Your Addresses</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col flex-1 min-h-0">
              <div className="p-4 flex-shrink-0">
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="w-full rounded-full bg-primary text-primary-foreground"
                      data-testid="create-address-btn-mobile"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Address
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Create New Email Address</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Username</Label>
                        <Input
                          placeholder="myusername"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                          className="bg-input/50 border-transparent focus:border-primary"
                          data-testid="new-username-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Domain</Label>
                        <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                          <SelectTrigger className="bg-input/50 border-transparent focus:border-primary" data-testid="domain-select">
                            <SelectValue placeholder="Select domain" />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border">
                            {domains.map(domain => (
                              <SelectItem key={domain.id} value={domain.id} data-testid={`domain-option-${domain.name}`}>
                                @{domain.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {newUsername && selectedDomain && (
                        <div className="p-3 rounded-lg bg-secondary/50 font-mono text-sm text-center">
                          {newUsername}@{domains.find(d => d.id === selectedDomain)?.name}
                        </div>
                      )}
                      <Button 
                        onClick={createAddress}
                        className="w-full rounded-full bg-primary text-primary-foreground"
                        data-testid="confirm-create-btn"
                      >
                        Create Address
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-2">
                  {addresses.map(address => (
                    <div
                      key={address.id}
                      className={`p-3 rounded-lg cursor-pointer transition-all mb-1 group ${
                        selectedAddress?.id === address.id 
                          ? 'bg-secondary border border-primary/50' 
                          : 'hover:bg-secondary/50'
                      }`}
                      onClick={() => {
                        setSelectedAddress(address);
                        setMobileMenuOpen(false);
                      }}
                      data-testid={`address-item-mobile-${address.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-sm truncate">{address.address}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {getTimeRemaining(address.expires_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); copyAddress(address.address); }}
                            data-testid={`copy-address-mobile-${address.id}`}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); deleteAddress(address.id); }}
                            data-testid={`delete-address-mobile-${address.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {addresses.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No addresses yet. Create one!
                    </div>
                  )}
                </div>
              </ScrollArea>
              <div className="p-4 border-t border-border flex-shrink-0">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user?.username}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {user?.is_admin && (
                    <Link to="/admin" className="flex-1">
                      <Button variant="outline" size="sm" className="w-full" data-testid="admin-panel-btn-mobile">
                        <Settings className="w-4 h-4 mr-1" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={logout}
                    data-testid="logout-btn-mobile"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:w-72 border-r border-border flex-col glass">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-6 h-6 text-primary" />
            <span className="font-heading text-lg font-semibold">Instant-Email</span>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="w-full rounded-full bg-primary text-primary-foreground hover:scale-[1.02] active:scale-[0.98] transition-all"
                data-testid="create-address-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Address
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading">Create New Email Address</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    placeholder="myusername"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                    className="bg-input/50 border-transparent focus:border-primary"
                    data-testid="new-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Domain</Label>
                  <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                    <SelectTrigger className="bg-input/50 border-transparent focus:border-primary" data-testid="domain-select">
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {domains.map(domain => (
                        <SelectItem key={domain.id} value={domain.id} data-testid={`domain-option-${domain.name}`}>
                          @{domain.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {newUsername && selectedDomain && (
                  <div className="p-3 rounded-lg bg-secondary/50 font-mono text-sm text-center">
                    {newUsername}@{domains.find(d => d.id === selectedDomain)?.name}
                  </div>
                )}
                <Button 
                  onClick={createAddress}
                  className="w-full rounded-full bg-primary text-primary-foreground"
                  data-testid="confirm-create-btn"
                >
                  Create Address
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Address List */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-2">
              Your Addresses ({addresses.length})
            </div>
            {addresses.map(address => (
              <div
                key={address.id}
                className={`p-3 rounded-lg cursor-pointer transition-all mb-1 group ${
                  selectedAddress?.id === address.id 
                    ? 'bg-secondary border border-primary/50' 
                    : 'hover:bg-secondary/50'
                }`}
                onClick={() => setSelectedAddress(address)}
                data-testid={`address-item-${address.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm truncate">{address.address}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {getTimeRemaining(address.expires_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={(e) => { e.stopPropagation(); copyAddress(address.address); }}
                      data-testid={`copy-address-${address.id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); deleteAddress(address.id); }}
                      data-testid={`delete-address-${address.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {addresses.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No addresses yet. Create one!
              </div>
            )}
          </div>
        </ScrollArea>

        {/* User Section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.username}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {user?.is_admin && (
              <Link to="/admin" className="flex-1">
                <Button variant="outline" size="sm" className="w-full" data-testid="admin-panel-btn">
                  <Settings className="w-4 h-4 mr-1" />
                  Admin
                </Button>
              </Link>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Message List */}
        <div className={`w-full lg:w-96 border-r border-border flex flex-col ${
          selectedMessage ? 'hidden lg:flex' : 'flex'
        }`}>
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="font-heading font-semibold">Inbox</h2>
              {selectedAddress && (
                <p className="text-xs text-muted-foreground font-mono truncate">{selectedAddress.address}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Badge variant="default" className="bg-primary">{unreadCount}</Badge>
              )}
              <Button 
                size="icon" 
                variant="ghost"
                onClick={refreshMessages}
                data-testid="refresh-inbox-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1">
            {selectedAddress ? (
              messages.length > 0 ? (
                <div className="p-2">
                  {messages.map(message => (
                    <div
                      key={message.id}
                      className={`p-3 rounded-lg cursor-pointer transition-all mb-1 group ${
                        selectedMessage?.id === message.id 
                          ? 'bg-secondary border border-primary/50' 
                          : 'hover:bg-secondary/50'
                      } ${!message.is_read ? 'border-l-2 border-l-primary' : ''}`}
                      onClick={() => viewMessage(message)}
                      data-testid={`message-item-${message.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {!message.is_read && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                            <p className={`text-sm truncate ${!message.is_read ? 'font-semibold' : ''}`}>
                              {message.from_address}
                            </p>
                          </div>
                          <p className="text-sm font-medium truncate mt-1">{message.subject}</p>
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {message.body?.substring(0, 50)}...
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(message.received_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); deleteMessage(message.id); }}
                            data-testid={`delete-message-${message.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <Inbox className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No messages yet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Emails sent to this address will appear here
                  </p>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <Mail className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Select an address</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Or create a new one to get started
                </p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Message Detail */}
        <div className={`flex-1 flex flex-col ${
          selectedMessage ? 'flex' : 'hidden lg:flex'
        }`}>
          {selectedMessage ? (
            <>
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="lg:hidden"
                    onClick={() => setSelectedMessage(null)}
                    data-testid="back-to-inbox-btn"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                  </Button>
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                    <MailOpen className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{selectedMessage.from_address}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(selectedMessage.received_at)}
                    </p>
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="hidden lg:flex"
                  onClick={() => setSelectedMessage(null)}
                  data-testid="close-message-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-6 border-b border-border">
                <h1 className="font-heading text-xl font-semibold">{selectedMessage.subject}</h1>
                <p className="text-sm text-muted-foreground mt-1">To: {selectedMessage.to_address}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {selectedMessage.html_body ? (
                  <div 
                    className="prose prose-invert max-w-none min-h-full [&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:table-auto"
                    dangerouslySetInnerHTML={{ __html: selectedMessage.html_body }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap text-foreground min-h-full">
                    {selectedMessage.body}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-20 h-20 rounded-full bg-secondary/50 flex items-center justify-center mb-6">
                <Shield className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-heading text-xl font-semibold mb-2">Your Privacy Matters</h2>
              <p className="text-muted-foreground max-w-md">
                Select a message to view its content. All emails are automatically deleted after 24 hours.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
